/**
 * Своя камера для чека (макет 14.10.2026, docs/design/scan-2026-10-14).
 *
 * «Сканировать» открывает камеру сразу, без листа с кнопками. Системную
 * камеру открыть можно, но в ней не написать, что делать, — поэтому камера
 * своя, с подсказкой прямо в кадре и подписанной кнопкой: сама она чек не
 * ловит, снимает человек.
 *
 * Качество — максимум, который отдаёт браузер: до 4K у новых iPhone. Снимка
 * в полном разрешении Safari сайтам не даёт (нет ImageCapture), но разница
 * пропадает и так: перед разбором любой снимок уменьшается до 2000 точек по
 * длинной стороне (`prepareReceipt`).
 *
 * Фокус — автоматический, касанием не наводится: Safari не даёт сайтам
 * управлять фокусом.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icon.js';

type Stage = 'starting' | 'live' | 'denied';

export function ReceiptCamera({ reading, onShot, onClose }: {
  /** Снимок разбирается: кадр замер, поверх — «Читаю чек…». */
  reading: boolean;
  /** Снимок с камеры или из галереи. */
  onShot: (file: File) => void;
  onClose: () => void;
}) {
  const [stage, setStage] = useState<Stage>(() => (typeof navigator.mediaDevices?.getUserMedia === 'function' ? 'starting' : 'denied'));
  const [still, setStill] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  /** Снимок уже есть: камера, открывшаяся позже, сразу гасится. */
  const taken = useRef(false);

  const stop = () => { stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null; };

  useEffect(() => {
    if (stage !== 'starting') return;
    let alive = true;
    void navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } },
    }).then(async (s) => {
      if (!alive || taken.current) { s.getTracks().forEach((t) => t.stop()); return; }
      stream.current = s;
      const v = video.current;
      if (v) { v.srcObject = s; await v.play().catch(() => {}); }
      setStage('live');
    }).catch(() => { if (alive) setStage('denied'); });
    return () => { alive = false; };
  }, [stage]);

  useEffect(() => () => { stop(); if (still) URL.revokeObjectURL(still); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Кадр целиком, в полном разрешении потока, — разбор уменьшит его сам. */
  const shoot = () => {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d')?.drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      taken.current = true;
      setStill(URL.createObjectURL(blob));
      stop();
      onShot(new File([blob], 'receipt.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  const gallery = (label: ReactNode, className: string) => (
    <label className={className}>
      {label}
      <input type="file" accept="image/*" onChange={(e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        taken.current = true;
        setStill(URL.createObjectURL(f));
        stop();
        onShot(f);
      }} />
    </label>
  );

  const close = () => { stop(); onClose(); };

  // Камеры нет — экран с объяснением; снимок из галереи уже разбирается — обычный экран с ним.
  if (stage === 'denied' && !reading) {
    return createPortal(
      <div className="rcam none" role="dialog" aria-modal="true" aria-label="Камера">
        <button className="rcam-close" aria-label="Закрыть" onClick={close}><Icon name="xmark" size={20} /></button>
        <div className="rcam-none">
          <div className="rcam-none-ico"><Icon name="camera" size={30} /></div>
          <b>Нет доступа к камере</b>
          <span>Разреши камеру для SKOKA в настройках телефона — или выбери снимок чека.</span>
          {gallery('Выбрать фото', 'btn pri file')}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className={`rcam${reading ? ' frozen' : ''}`} role="dialog" aria-modal="true" aria-label="Камера">
      <video ref={video} className="rcam-video" playsInline muted autoPlay />
      {still && <img className="rcam-video" src={still} alt="" />}
      <div className="rcam-shade" />
      <div className="rcam-frame" aria-hidden="true"><span /><span /><span /><span /></div>
      <button className="rcam-close" aria-label="Закрыть" onClick={close}><Icon name="xmark" size={20} /></button>
      <div className="rcam-hint" aria-live="polite">
        {reading
          ? <><b>Читаю чек…</b><span>Обычно несколько секунд</span></>
          : <><b>Наведи камеру на чек<br />и нажми «Снять»</b><span>Весь чек — в рамке, сверху и при ровном свете</span></>}
      </div>
      {reading ? <div className="rcam-work" aria-hidden="true" /> : (
        <div className="rcam-bar">
          {gallery(<><Icon name="images" size={22} /><span>Из галереи</span></>, 'rcam-gal')}
          <button className="rcam-shutter" aria-label="Снять" disabled={stage !== 'live'} onClick={shoot}><i /></button>
          <span className="rcam-shot">Снять</span>
        </div>
      )}
    </div>,
    document.body,
  );
}
